import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Eye } from "@phosphor-icons/react";
import AuditBar from "@/components/site/AuditBar";

const HERO_SITES = [
  {
    id: "scrapmans",
    label: "Scrapman's Metals",
    desktop: "/portfolio/scrapmans-metals-desktop.jpg",
    mobile: "/portfolio/scrapmans-metals-mobile.jpg",
    alt: "Scrapman's Metal Recycling — website built by WebJesters",
    bg: "bg-white",
  },
  {
    id: "cabreset",
    label: "Cab Reset AZ",
    desktop: "/portfolio/interior-reset-desktop.jpg",
    mobile: "/portfolio/interior-reset-mobile.jpg",
    alt: "Cab Reset AZ — auto detailing website built by WebJesters",
    bg: "bg-black",
  },
  {
    id: "sonoran",
    label: "Sonoran Clean",
    desktop: "/portfolio/sonoran-clean-desktop.jpg",
    mobile: "/portfolio/sonoran-clean-mobile.jpg",
    alt: "Sonoran Clean — pressure washing website built by WebJesters",
    bg: "bg-black",
  },
];
const ROTATE_MS = 5500;

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.07 },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-36 md:pt-40 pb-10 sm:pb-16 overflow-hidden"
    >
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-70" />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 flex flex-col items-center text-center">
        <motion.div
          initial="hidden"
          animate="show"
          custom={0}
          variants={fadeUp}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] pl-2 pr-4 py-1.5 text-[12px] text-zinc-300"
          data-testid="hero-eyebrow"
        >
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-brand-500 pulse-dot" />
          </span>
          <span className="tracking-[0.18em] uppercase text-zinc-400 text-[10.5px]">
            <span className="sm:hidden">Websites · Google · Local Visibility</span>
            <span className="hidden sm:inline">Websites · Google Business Profile · Local Visibility</span>
          </span>
        </motion.div>

        <motion.h1
          initial="hidden"
          animate="show"
          custom={1}
          variants={fadeUp}
          data-testid="hero-headline"
          className="mt-7 sm:mt-8 max-w-[15ch] font-display font-bold text-[42px] sm:text-6xl lg:text-7xl xl:text-[92px] leading-[1.0] sm:leading-[0.98] tracking-tighter text-white"
        >
          Your website and Google presence.{" "}
          <span className="text-brand-400">Handled.</span>
        </motion.h1>

        <motion.p
          initial="hidden"
          animate="show"
          custom={2}
          variants={fadeUp}
          data-testid="hero-subheadline"
          className="mt-6 sm:mt-7 max-w-3xl text-[16px] sm:text-lg text-zinc-300 leading-relaxed"
        >
          Websites built and hosted for you. Google Business Profiles set up right and kept active.
          Automations that answer inquiries while you work.
        </motion.p>

        <motion.div
          initial="hidden"
          animate="show"
          custom={3}
          variants={fadeUp}
          className="mt-8 sm:mt-9 flex flex-col sm:flex-row gap-3 w-full sm:w-auto"
        >
          <a
            href="#contact"
            data-testid="hero-cta-quote"
            className="group inline-flex items-center justify-center gap-3 pl-7 pr-2 py-2 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
          >
            Get a quote
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-600 text-white transition-transform group-hover:translate-x-0.5">
              <ArrowUpRight size={16} weight="bold" />
            </span>
          </a>
          <a
            href="#work"
            data-testid="hero-cta-view-work"
            className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/15 bg-white/[0.03] text-white font-medium hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors"
          >
            <Eye size={18} weight="regular" />
            See live client sites
          </a>
        </motion.div>

        <motion.p
          initial="hidden"
          animate="show"
          custom={4}
          variants={fadeUp}
          data-testid="hero-trust-line"
          className="mt-6 text-[12.5px] text-zinc-400 tracking-wide"
        >
          Arizona-based · Month-to-month, no contracts · Replies within one business day
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 36 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full mt-12 sm:mt-16"
          data-testid="hero-mockup"
        >
          <DeviceCluster />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full mt-2 sm:mt-0"
        >
          <AuditBar />
        </motion.div>
      </div>
    </section>
  );
}

function FadeStack({ active, kind, className }) {
  return (
    <div className={`relative w-full h-full ${className}`}>
      {HERO_SITES.map((s, i) => (
        <motion.img
          key={s.id}
          src={s[kind]}
          alt={`${s.alt}, ${kind} view`}
          width={kind === "desktop" ? 3200 : 780}
          height={kind === "desktop" ? 1800 : 1356}
          loading="eager"
          fetchPriority={i === 0 ? "high" : "auto"}
          decoding="async"
          data-testid={`hero-${kind}-${s.id}`}
          initial={false}
          animate={{ opacity: i === active ? 1 : 0, scale: i === active ? 1 : 1.02 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className={`absolute inset-0 w-full h-full object-cover object-top ${s.bg}`}
          style={{ zIndex: i === active ? 2 : 1 }}
        />
      ))}
    </div>
  );
}

function DeviceCluster() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const onVis = () => document.hidden || setActive((a) => (a + 1) % HERO_SITES.length);
    const id = setInterval(onVis, ROTATE_MS);
    return () => clearInterval(id);
  }, [reduce]);

  const site = HERO_SITES[active];

  return (
    <div className="relative w-full max-w-[520px] sm:max-w-[760px] lg:max-w-[980px] mx-auto pb-10 sm:pb-16 pt-4 px-2 sm:px-0">
      <div className="absolute -inset-x-10 -bottom-4 h-32 rounded-[50%] bg-brand-700/25 blur-3xl pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="laptop-mockup is-thin relative z-10"
      >
        <div className="screen relative bg-black">
          <FadeStack active={active} kind="desktop" />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 24, y: 14 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="absolute -bottom-2 right-0 sm:-bottom-4 sm:-right-4 lg:-right-10 w-[104px] sm:w-[150px] lg:w-[190px] z-20"
      >
        <div className="phone-mockup">
          <div className="notch" />
          <div className="screen bg-black">
            <FadeStack active={active} kind="mobile" />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        data-testid="hero-live-chip"
        className="absolute -top-2 left-3 sm:left-6 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl glass border border-white/10 text-[10.5px] sm:text-[11px] text-zinc-200 flex items-center gap-1.5 sm:gap-2 z-30"
      >
        <span className="block w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
        <span className="whitespace-nowrap">Live ·</span>
        <span className="relative inline-grid">
          {HERO_SITES.map((s, i) => (
            <motion.span
              key={s.id}
              data-testid={`hero-live-label-${s.id}`}
              initial={false}
              animate={{ opacity: i === active ? 1 : 0, y: i === active ? 0 : 4 }}
              transition={{ duration: 0.5 }}
              className="col-start-1 row-start-1 whitespace-nowrap"
              aria-hidden={i !== active}
            >
              {s.label}
            </motion.span>
          ))}
        </span>
      </motion.div>

      <div
        data-testid="hero-rotation-dots"
        className="absolute bottom-2 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-30"
      >
        {HERO_SITES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            aria-label={`Show ${s.label}`}
            aria-pressed={i === active}
            data-testid={`hero-dot-${s.id}`}
            onClick={() => setActive(i)}
            className="group/dot inline-flex items-center justify-center h-6 min-w-6 px-1"
          >
            <span
              className={`block h-1 rounded-full transition-all duration-500 ${
                i === active ? "w-6 bg-white/80" : "w-2 bg-white/25 group-hover/dot:bg-white/50"
              }`}
            />
          </button>
        ))}
      </div>
      <span className="sr-only" aria-live="polite">Showing {site.label}</span>
    </div>
  );
}
